import type { Column, Table } from "@tanstack/react-table";
import { Settings2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

interface ColumnVisibilityControlProps<TData> {
  table: Table<TData>;
}

/* Bu ustunlar ro'yxatda ko'rsatilmaydi: tartib raqami va amallar ustuni
   jadvalning o'zi, ma'lumot emas — ularni yashirish jadvalni buzardi. */
const ALWAYS_VISIBLE = new Set(["actions", "id", "index"]);

/**
 * Ustun ma'lumot emas, qatorning o'zimi?
 *
 * Sarlavhasi bo'sh ko'rsatuv ustuni — ochish strelkasi yoki tanlov katagi:
 * ro'yxatda uni atab bo'lmaydi va yashirilsa qator ochilmaydigan yoki
 * tanlanmaydigan bo'lib qolardi.
 */
const isStructural = <TData,>(column: Column<TData>) => {
  const header = column.columnDef.header;

  if (ALWAYS_VISIBLE.has(column.id)) return true;
  if (header === "") return true;

  return !column.accessorFn && typeof header !== "string";
};

/**
 * Ustun ko'rinishi tugmasi — faqat chizadi va almashtiradi.
 *
 * Tanlovni saqlash bu yerda emas, `useColumnVisibility` da: holatni jadvalga
 * bergan sahifaning o'zi uni `localStorage` ga yozadi.
 */
export function ColumnVisibilityControl<TData>({
  table,
}: Readonly<ColumnVisibilityControlProps<TData>>) {
  const { t } = useTranslation();

  const columns = table.getAllLeafColumns().filter(column => !isStructural(column));
  const hiddenCount = columns.filter(column => !column.getIsVisible()).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="relative flex size-8 items-center gap-2"
          aria-label={t("TABLE.COLUMN_VISIBILITY")}
        >
          <Settings2 className="size-4" />
          {/* Yashirilgan ustunlar soni — popover yopiq turganda jadval nega
              to'liq emasligini boshqa hech narsa aytmaydi. */}
          {hiddenCount > 0 && (
            <Badge className="pointer-events-none absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 text-[10px] tabular-nums">
              {hiddenCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-52 gap-0 p-1" align="end">
        <div className="flex flex-col gap-1">
          {columns.map(column => {
            const header = column.columnDef.header;
            const label = typeof header === "string" ? header : column.id;

            return (
              <Label
                key={column.id}
                className="hover:bg-secondary flex cursor-pointer items-center gap-2 rounded-md p-1 px-2 transition-all duration-200"
              >
                <Checkbox
                  className="rounded-[4px]"
                  checked={column.getIsVisible()}
                  onCheckedChange={checked => column.toggleVisibility(!!checked)}
                />
                <span className="truncate text-xs">{label}</span>
              </Label>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
